import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

import { STATISTIC_CONST, TASK_CONST } from '@/constant';
import {
  useProjectFrameStateQuery,
  useProjectObjectQuery,
  useProjectPeriodMetricQuery,
  useSuperObjectClassListQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  Groups,
  History,
  Info,
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VideoLabel,
} from '@/package';
import type { FrameStateStatistic, Project } from '@/type';

import {
  SelectablePieChart,
  calculateExtendClassData,
  calculateExtendClassDataWithBaseClass,
  calculatePeriodData,
  calculateTotalClassData,
  calculateTotalFrame,
  calculateTotalObject,
  calculateUserCount,
  filterUnknownClass,
} from '../Statistic';
import { filterSuperClassList } from '../Statistic';
import BarChart from '../Statistic/BarChart/BarChart';
import { DoughnutChart } from '../Statistic/DoughnutChart';
import { RangePicker } from '../Statistic/RangePicker';
import { StaticPieChart } from '../Statistic/StaticPieChart';
import { StatisticTable } from '../Statistic/StatisticTable';
import { useSelectedIndex } from '../Statistic/hook';
import type { PeriodUnit } from '../Statistic/type';

const ProjectStatistics = ({ project }: { project: Project }) => {
  return (
    <VStack gap={1.5} className="h-full w-full overflow-y-auto bg-white p-4">
      <ProjectStatisticOverview project={project} />
      <ProjectTotalLabeledObject project={project} />
      <ProjectPeriodLabeledObject project={project} />
    </VStack>
  );
};

const ProjectStatisticOverview = ({ project }: { project: Project }) => {
  const { data: projectState } = useProjectFrameStateQuery(project.id);
  const { data: projectObject } = useProjectObjectQuery(project.id, true);

  const totalObject = calculateTotalObject(projectObject?.statExtendClass);
  const frameProgress = (projectState as FrameStateStatistic) ?? {
    prework: 0,
    working: 0,
    reject: 0,
    review: 0,
    reviewing: 0,
    done: 0,
  };
  const totalFrame = calculateTotalFrame(frameProgress);
  const totalUser = calculateUserCount(
    project?.user,
    (cur: { role: string }) => cur.role,
  );

  const taskProgress = project.task?.reduce(
    (acc, cur) => {
      return {
        none: acc.none + (cur.state === TASK_CONST.TASK_WORKSTATE.NONE ? 1 : 0),
        pending:
          acc.pending +
          (cur.state === TASK_CONST.TASK_WORKSTATE.PENDING ? 1 : 0),
        ready:
          acc.ready + (cur.state === TASK_CONST.TASK_WORKSTATE.READY ? 1 : 0),
        progress:
          acc.progress +
          (cur.state === TASK_CONST.TASK_WORKSTATE.PROGRESS ? 1 : 0),
        completed:
          acc.completed +
          (cur.state === TASK_CONST.TASK_WORKSTATE.COMPLETED ? 1 : 0),
        fail: acc.fail + (cur.state === TASK_CONST.TASK_WORKSTATE.FAIL ? 1 : 0),
      };
    },
    { none: 0, pending: 0, ready: 0, progress: 0, completed: 0, fail: 0 },
  ) ?? { none: 0, pending: 0, ready: 0, progress: 0, completed: 0, fail: 0 };

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Overview</div>
      <div className="grid h-[190px] grid-cols-2 gap-[14px]">
        <VStack className="h-full w-full" gap={1}>
          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <Groups className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Members</span>
                <VFlex
                  className="text-body-md text-gray-300"
                  items="center"
                  gap={0.25}
                >
                  <span>Manager</span>
                  <span className="text-body-lg font-semibold text-black">
                    {Intl.NumberFormat().format(totalUser.manager)}
                  </span>
                  <div className="h-4 w-px bg-gray-300" />
                  <span>Labeler</span>
                  <span className="text-body-lg font-semibold text-black">
                    {Intl.NumberFormat().format(totalUser.worker)}
                  </span>
                </VFlex>
              </VStack>
            </VFlex>

            <VFlex
              gap={0.5}
              className="flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Total tasks</span>
                <span className="text-body-lg font-semibold text-black">
                  {project.task?.length}
                </span>
              </VStack>
            </VFlex>
          </VFlex>

          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Work Frames</span>
                <span className="text-body-lg font-semibold text-black">
                  {totalFrame}
                </span>
              </VStack>
            </VFlex>

            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <CenterFocusWeak className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">
                  Total Objects
                </span>
                <span className="text-body-lg font-semibold text-black">
                  {totalObject}
                </span>
              </VStack>
            </VFlex>
          </VFlex>
        </VStack>
        <VStack gap={0.5} className="rounded-md px-4 py-2 shadow-sm">
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total tasks progress
            </VFlex>

            <div className="text-body-sm text-gray-600">
              Total {project.task?.length ?? 0}
            </div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={project.task?.length ?? 0}
              taskProgress={taskProgress}
            />
          </VFlex>
        </VStack>
      </div>
    </VStack>
  );
};

const ProjectTotalLabeledObject = ({ project }: { project: Project }) => {
  const { selectedIndex, toggleSelect, clearSelection } = useSelectedIndex();
  const { data: projectObject } = useProjectObjectQuery(project.id, true);
  const { data: allSuperClassList } = useSuperObjectClassListQuery();

  const toShowSuperClassList = filterSuperClassList(
    allSuperClassList?.filter(filterUnknownClass),
    project.extendClass,
  );

  const projectTotalData = calculateTotalClassData(
    toShowSuperClassList,
    projectObject?.statExtendClass,
  );

  const extendClassData =
    typeof selectedIndex === 'number'
      ? calculateExtendClassData(
          toShowSuperClassList.at(selectedIndex)?.extendClass,
          projectObject?.statExtendClass,
        )
      : [];

  const extendClassDataWithBaseClass =
    typeof selectedIndex === 'number' && projectTotalData[selectedIndex]
      ? calculateExtendClassDataWithBaseClass(
          projectTotalData[selectedIndex],
          extendClassData,
        )
      : [];

  return (
    <VStack gap={0.5} className="w-full">
      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-md font-bold text-blue-300">
          Total labeled objects
        </div>
        <VFlex items="center" justify="end" gap={0.25} className="flex-1">
          <Info className="size-4 text-info-500" />
          <div className="text-body-md">
            Clicking a pie chart slice or table item in the base class also
            allows you to view statistics for its extension classes.
          </div>
        </VFlex>
      </VFlex>
      <VFlex gap={2} items="center" justify="center">
        <SelectablePieChart
          pieData={projectTotalData}
          selectedValue={selectedIndex}
          onSelect={toggleSelect}
        />
        <StatisticTable
          data={projectTotalData}
          selectedValue={selectedIndex}
          onRowClick={toggleSelect}
        />
      </VFlex>

      {typeof selectedIndex === 'number' && extendClassData.length > 0 && (
        <VFlex gap={2} items="center" justify="center">
          <StaticPieChart
            pieData={extendClassDataWithBaseClass}
            onClose={clearSelection}
          />
          <StatisticTable data={extendClassDataWithBaseClass} />
        </VFlex>
      )}
    </VStack>
  );
};

const ProjectPeriodLabeledObject = ({ project }: { project: Project }) => {
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('day');
  const [between, setBetween] = useState<[Dayjs, Dayjs]>([
    dayjs(STATISTIC_CONST.TODAY).subtract(12, 'week'),
    dayjs(STATISTIC_CONST.TODAY),
  ]);
  const { data: taskMetric } = useProjectPeriodMetricQuery({
    projectID: project.id,
    period: periodUnit,
    between,
  });

  const extendClassList = taskMetric?.extendClass?.filter(filterUnknownClass);
  const periodData = calculatePeriodData(
    extendClassList,
    taskMetric?.userMetric,
  );

  return (
    <VStack gap={0.5} className="h-full w-full">
      <div className="text-headline-md font-bold text-blue-300">
        Labeled objects statistics
      </div>

      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-sm font-semibold text-primary-500">
          Trend
        </div>
        <VFlex items="center" className="w-full" justify="end" gap={0.5}>
          <RangePicker
            value={between}
            onChange={setBetween}
            periodUnit={periodUnit}
            key={periodUnit}
          />

          <VTab
            defaultValue="day"
            onValueChange={val => setPeriodUnit(val as PeriodUnit)}
            value={periodUnit}
            size="small"
            variant="secondary"
          >
            <VTabTriggerContainer>
              <VTabTrigger value="day">Daily</VTabTrigger>
              <VTabTrigger value="week">Weekly</VTabTrigger>
              <VTabTrigger value="month">Monthly</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </VFlex>

      {periodData.length > 0 && (
        <BarChart data={periodData} periodUnit={periodUnit} between={between} />
      )}
    </VStack>
  );
};

export default ProjectStatistics;
